import {
  CommonActions,
  NavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { Platform } from 'react-native';

import {
  useNavigateToChatDrawer,
  useWebDrawer,
} from '@components/Web/Drawer/WebDrawerContext';

import type { Project, Task, User } from '@graphql/generated';
import type { AppStackParamList as AppStackParamListNative } from '@navigation/app-stack';
import type { AppStackParamList as AppStackParamListWeb } from '@navigation/app-stack.web';
import type { DistinctUnion } from 'types';

type AppStackParamList = DistinctUnion<
  AppStackParamListNative,
  AppStackParamListWeb
>;

/**
 * Returns a navigation object that
 * @returns An object that allows navigation within the application
 */
export const useAppNavigation = <
  T extends NavigationProp<AppStackParamList> =
    NavigationProp<AppStackParamList>,
>() => {
  const navigateToChatDrawer = useNavigateToChatDrawer();
  const navigation = useNavigation<T>();
  const {
    setIsTaskWebPanelOpen,
    setIdTaskDetailOpen,
    setTaskNameOfTaskDetailOpen,
    setProjectNameOfTaskDetailOpen,
  } = useWebDrawer();

  const nav = {
    ...navigation,
    /**
     * Navigates to a specific project.
     * @param project The project to navigate
     */
    navigateToProject: (
      project: Pick<Project, 'id' | 'uuid'>,
      resetStack?: boolean,
      showOnboardingSuccessModal?: boolean
    ) => {
      // Project details overrides this value on initial load.
      // We want to only set a new selected project if we are
      // navigating to a new project.
      if (Platform.OS === 'web') {
        if (!resetStack) {
          navigation.navigate('projects-stack', {
            screen: 'details',
            params: { projectUUId: project.uuid },
          });
        } else {
          navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [
                {
                  name: 'projects-stack',
                  params: {
                    screen: 'details',
                    params: { projectUUId: project.uuid },
                  },
                },
              ],
            })
          );
        }
      } else {
        const routeParams: AppStackParamList['projects-stack'] = {
          projectUUId: project.uuid,
          screen: 'project-details',
          params: {
            projectUUId: project.uuid,
            ...(showOnboardingSuccessModal
              ? { showOnboardingSuccessModal: true }
              : {}),
          },
        };
        if (!resetStack) {
          navigation.navigate('projects-stack', routeParams);
        } else {
          navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [{ ...routeParams, name: 'project-details' }],
            })
          );
        }
      }
    },

    /**
     * Navigates to the project list.
     *
     * Used when there is no single project to open — e.g. the aggregated
     * "added to N projects" toast, where picking one of the N would be
     * arbitrary.
     */
    navigateToProjectsList: () => {
      // `AppStackParamList` is a DistinctUnion of the native and web param
      // lists, so nested `screen` params don't narrow — every `navigate` in
      // this file has the same problem. Cast rather than add another error to
      // the pile.
      const navigateUntyped = navigation.navigate as unknown as (
        screen: string,
        params?: object
      ) => void;

      if (Platform.OS === 'web') {
        navigateUntyped('projects-stack', { screen: 'allprojects' });
      } else {
        navigateUntyped('home', { screen: 'projects-home' });
      }
    },

    /**
     * Navigates to a specific task.
     * @param task The task to navigate
     */
    navigateToTask: (
      task: Pick<Task, 'id' | 'projectId'> & {
        name?: string;
        project?: { name?: string; uuid?: string };
      },
      resetStack?: boolean,
      openMyTasks?: boolean
    ) => {
      // Task details overrides this value on initial load.
      // We want to only set a new selected task if we are
      // navigating to a new task.

      if (Platform.OS === 'web') {
        // Open my tasks
        openMyTasks && navigation.navigate('tasks-stack');

        // Open task panel
        setIdTaskDetailOpen(task.id);
        // Carried through as a display-only fallback for the task-panel's
        // no-access/not-found state, since that query can't return a name.
        setTaskNameOfTaskDetailOpen(task.name ?? '');
        setProjectNameOfTaskDetailOpen(task.project?.name ?? '');
        setIsTaskWebPanelOpen(true);
      } else {
        const routeParams: AppStackParamList['tasks-stack'] = {
          taskId: task.id,
          screen: 'task-detail',
          params: {
            taskId: task.id,
            projectUUId: task?.project?.uuid,
          },
        };
        if (!resetStack) {
          navigation.navigate('tasks-stack', routeParams);
        } else {
          navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [{ ...routeParams, name: 'task-detail' }],
            })
          );
        }
      }
    },

    /**
     * Navigates to a specific task within the context of a project.
     *
     * On native, this navigates directly to the task details.
     * @param task The task to navigate
     */
    navigateToProjectTask: (task: Pick<Task, 'id' | 'projectId'>) => {
      if (Platform.OS === 'web') {
        navigation.navigate('projects-stack', {
          screen: 'details',
          params: {
            projectId: task.projectId,
            screen: 'tasks',
            params: {
              projectId: task.projectId,
              taskId: task.id,
            },
          },
        });
      } else {
        nav.navigateToTask(task);
      }
    },

    navigateToProjectChecklist: (project: Pick<Project, 'id' | 'uuid'>) => {
      if (Platform.OS === 'web') {
        navigation.navigate('projects-stack', {
          screen: 'details',
          params: {
            projectUUId: project.uuid,
            screen: 'checklist',
            params: {
              projectUUId: project.uuid,
            },
          },
        });
      } else {
        // nav.navigateToTask(task);
      }
    },

    /** Navigates to a screen in the My profile. */
    navigateToMyprofileSubscriptionDetail: (
      uuid: string,
      teamStatusOrOptions?:
        | {
            teamStatus?: unknown;
            openAfterCreate?: boolean;
          }
        | unknown,
      openAfterCreate = false
    ) => {
      const isOptionsObject =
        typeof teamStatusOrOptions === 'object' &&
        teamStatusOrOptions !== null &&
        !Array.isArray(teamStatusOrOptions);

      const resolvedTeamStatus = isOptionsObject
        ? teamStatusOrOptions.teamStatus
        : teamStatusOrOptions;
      const resolvedOpenAfterCreate = isOptionsObject
        ? (teamStatusOrOptions.openAfterCreate ?? false)
        : openAfterCreate;

      if (Platform.OS === 'web') {
        navigation.navigate('my-account', {
          screen: 'subscriptionPlanDetail',
          params: {
            teamUUId: uuid,
            teamStatus: resolvedTeamStatus,
            openAfterCreate: resolvedOpenAfterCreate,
          },
        });
      }
    },

    /** Navigates to a screen in the chat drawer. */
    navigateToChatDrawer,

    navigateToContact: (contact: Pick<User, 'id'>) => {
      if (Platform.OS === 'web') {
        navigation.navigate('contacts-stack', {
          screen: 'contacts',
          params: {
            screen: 'contacts',
            params: {
              userId: contact.id,
            },
          },
        });
      }
    },
    navigateToProjectFiles: (
      project: Pick<Project, 'id' | 'uuid'>,
      resetStack?: boolean
    ) => {
      if (Platform.OS === 'web') {
        if (!resetStack) {
          navigation.navigate('files-stack', {
            screen: 'project-files',
            params: { projectId: project.id, projectUUId: project.uuid },
          });
        } else {
          navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [
                {
                  name: 'files-stack',
                  params: {
                    screen: 'project-files',
                    params: {
                      projectId: project.id,
                      projectUUId: project.uuid,
                    },
                  },
                },
              ],
            })
          );
        }
      } else {
        const routeParams: AppStackParamList['files-stack'] = {
          projectId: project.id,
          projectUUId: project.uuid,
          screen: 'project-files',
          params: {
            projectId: project.id,
            projectUUId: project.uuid,
          },
        };
        if (!resetStack) {
          navigation.navigate('files-stack', routeParams);
        } else {
          navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [{ ...routeParams, name: 'project-files' }],
            })
          );
        }
      }
    },
  };

  return nav;
};
